/**
 * Data Reliability v2 — type contract.
 *
 * Per DEV-846 epic decisions: two visible composites (Now, Outlook) made of
 * named components, each with a lifecycle-aware applicability predicate.
 *
 * Pure types only — no engine logic, no I/O. Components and the main
 * computeDataReliability() entry live in sibling files.
 *
 * See: DEV-846 epic + DEV-847 design sketch.
 */

/* ------------------------------------------------------------------------- */
/*  Component & composite results                                            */
/* ------------------------------------------------------------------------- */

export type ComponentKey =
  | 'completeness'
  | 'timeliness'
  | 'entry_latency_trend'
  | 'engagement'
  | 'drift_risk'
  | 'preflight_risk';

export type CompositeKey = 'now' | 'outlook';

/**
 * Single component's contribution to a composite.
 *
 * `applicable: false` means the component's lifecycle gate is not met OR it
 * has insufficient data to score (e.g., PreFlightRisk when no PreFlight items
 * are configured at all). Inactive components do NOT participate in the
 * composite weighted average — their weight is redistributed across the
 * remaining applicable components.
 */
export interface ComponentResult {
  key: ComponentKey;
  /** 0-100 when applicable; null when not applicable. */
  score: number | null;
  applicable: boolean;
  /** When `applicable: false`, a one-line reason for surfacing in drill-in tooltips. */
  reason?: string;
  /**
   * Post-renormalization weight (only meaningful when `applicable: true`).
   * If composite has 4 applicable components with configured weights
   * 25/25/25/25, each renormalized weight is 0.25. If 1 component is
   * inactive, the remaining 3 each renormalize to ~0.33.
   */
  weight_normalized?: number;
  /**
   * Human-readable one-liner for drill-in tooltips.
   * e.g. "3 of 4 entries on time" / "Project owner hasn't acked tasks for 12 days"
   */
  explain: string;
}

/**
 * Now (lagging) or Outlook (leading) composite.
 *
 * `applicable: false` at the composite level means the entire composite's
 * lifecycle gate failed (e.g., Now when no reporting period has elapsed).
 * UI renders these as "N/A" with the reason text in place of the score.
 */
export interface CompositeResult {
  key: CompositeKey;
  /** Weighted average across applicable components, 0-100; null when composite is N/A. */
  score: number | null;
  /** Standard 0-3 color band. 0 = grey/N/A, 1 = red, 2 = yellow, 3 = green. */
  color: 0 | 1 | 2 | 3;
  /** -1 down, 0 flat, 1 up. Computed from delta vs previous reporting period if available, else 0. */
  trend: -1 | 0 | 1;
  applicable: boolean;
  /** When `applicable: false`, why. Surfaced in the headline N/A explainer. */
  reason?: string;
  /**
   * All components for this composite, including inactive ones.
   * UI uses this for the drill-in breakdown — inactive components show
   * as "N/A" with their `reason` text rather than being hidden.
   */
  components: ComponentResult[];
}

/**
 * Top-level engine output. Single `computeDataReliability()` call produces
 * both composites plus a backward-compat single score for legacy consumers.
 */
export interface DataReliabilityResult {
  as_of_date: string;
  project_guid: string;
  now: CompositeResult;
  outlook: CompositeResult;
  /**
   * Backward-compat single composite for legacy callers (KpiSummaryHeader,
   * KpiGridTable, MyProjects map, GroupProgressPage, Portfolio rollup).
   * Strategy: prefer Now when applicable, fall back to Outlook, else null.
   *
   * DEV-851 migrates each of those consumers to display Now and Outlook
   * explicitly; this field exists to prevent breakage during migration.
   */
  legacy_composite_score: number | null;
  legacy_composite_color: 0 | 1 | 2 | 3;
  /**
   * Summary-rollup score: per DEV-846 epic decision, Summary uses Now only.
   * Convenience field exposed here so engine.ts doesn't have to reach into
   * `result.now.score` and re-handle the null case.
   */
  summary_score: number | null;
  summary_trend: -1 | 0 | 1;
}

/* ------------------------------------------------------------------------- */
/*  Inputs (everything the engine needs by value)                            */
/* ------------------------------------------------------------------------- */

export type Cadence = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export type ProjectStatus =
  | 'In Progress'
  | 'Completed'
  | 'On Hold'
  | 'Cancelled'
  | 'Planning'
  | string; // permissive — DB may carry historical statuses we shouldn't reject

export interface ProjectMetadataInput {
  status: ProjectStatus;
  /** ISO YYYY-MM-DD; null when not configured. */
  start_date: string | null;
  end_date: string | null;
  /**
   * Count of RACI assignments on the project (any role, any task).
   * Outlook lifecycle gate fails when this is 0 — engagement signal
   * has no meaning if no one is assigned to be engaged.
   */
  raci_assignment_count: number;
}

export interface ProgressEntryInput {
  category: 'schedule' | 'finance' | 'mission';
  /** ISO YYYY-MM-DD — the period this entry reports on. */
  reporting_date: string;
  /** ISO timestamp — when the entry was submitted. */
  created_at: string;
}

/**
 * DEV-851 (per-instance refactor): Per-instance summary of the latest
 * reporting state. App.tsx assembles one of these per configured KPI
 * instance per project from kpiConfigStore + the per-instance series stores.
 *
 * The Now components (Completeness + Timeliness) operate on this granularity
 * — matching exactly what the DataReliabilityTab matrix shows row-by-row.
 *
 * Why per-instance instead of per-category:
 * Category-level Completeness rounds up — if Schedule has 5 instances and
 * only 1 reports, Completeness still reads "Schedule category reported" and
 * the missing 4 are invisible. Per-instance fixes that: each instance is its
 * own line item in the score.
 *
 * `latest_reporting_date` / `latest_created_at` are the timestamps from the
 * MOST RECENT entry for this instance (with non-null actual value). Both
 * null when the instance has never been reported.
 */
export interface ExpectedInstanceInput {
  category: 'schedule' | 'finance' | 'mission';
  instance_id: string;
  cadence: Cadence;
  /** Per-instance grace; falls back to inputs.grace_days when null. */
  grace_days?: number;
  latest_reporting_date: string | null;
  latest_created_at: string | null;

  // ── DEV-851 follow-up — fractional coverage fields ─────────────
  // Populated by buildExpectedInstancesForProject when allDatesIndices +
  // project_start_date are passed in. Drives the new Missing column in
  // the DataReliabilityTab matrix and the fractional completeness rule
  // in computeCompleteness(). When undefined, completeness falls back
  // to the legacy per-instance recency check.

  /**
   * ISO date the cadence clock started for this instance. Resolved by
   * the selector as the latest of (tracking_start_date, project_start_date,
   * created_at). Future date → 'not-yet-due'.
   */
  cadence_start?: string | null;

  /** Total cadence periods elapsed from cadence_start to as_of_date. */
  expected_periods?: number;

  /** Distinct periods (buckets) that have at least one submission. */
  reported_periods?: number;

  /** expected_periods - reported_periods. Powers the matrix Missing column. */
  missing_periods?: number;

  /**
   * Average submission lag in days, across all reported entries for this
   * instance. lag_i = max(0, created_at - reporting_date). Drives the
   * matrix's "Time" column. null/undefined when no entries exist.
   */
  avg_lag_days?: number | null;
}

export interface DigestAckInput {
  user_guid: string;
  gantt_task_id: string;
  role_slot_guid: string;
  /** ISO timestamp. */
  acknowledged_at: string;
}

export type PreflightItemState =
  | 'draft'
  | 'seeking'
  | 'blocked'
  | 'partial'
  | 'met'
  | 'withdrawn';

export type PreflightItemPriority = 'critical' | 'important' | 'nice_to_have';

export interface PreflightItemInput {
  state: PreflightItemState;
  priority: PreflightItemPriority;
}

/**
 * Phase summary derived from existing PhaseSummaryTimeline computation
 * (the WARNING_GAP = 15 elapsed-vs-actual heuristic). The engine doesn't
 * recompute phase status — it consumes the same status values the timeline
 * already shows in the UI.
 */
export interface PhaseSummaryInput {
  phase_count: number;
  /** Phases where actual % is 15+ points behind elapsed time %. */
  warning_count: number;
  /** Phases that are critical (overdue or severely behind). */
  critical_count: number;
}

/**
 * Per-project component weights (loaded from projectKpiConfigOverrides;
 * defaults applied upstream by App.tsx before passing to engine).
 *
 * Each composite's weights must sum to 1.0 (caller-enforced; engine
 * renormalizes defensively if they don't).
 */
export interface ComponentWeights {
  now: {
    completeness: number;
    timeliness: number;
  };
  outlook: {
    entry_latency_trend: number;
    engagement: number;
    drift_risk: number;
    preflight_risk: number;
  };
}

/**
 * Reporting cadence per category. Drives Completeness's "expected entries
 * this period" computation. Categories with no configured cadence are
 * excluded from the expected count.
 */
export interface ReportingCadenceInput {
  schedule?: Cadence;
  finance?: Cadence;
  mission?: Cadence;
}

/**
 * Single argument bundle for computeDataReliability. App.tsx assembles this
 * from various data sources before calling the engine.
 *
 * Engine is pure: same inputs + same as_of_date = same result.
 * Retroactive reconstruction (DEV-850 trend sparkline) is achieved by the
 * caller filtering inputs to data with `created_at <= asOfDate` before the call.
 */
export interface DataReliabilityInputs {
  project_guid: string;
  as_of_date: string;
  project_metadata: ProjectMetadataInput;
  progress_entries: ProgressEntryInput[];
  /**
   * DEV-851: Per-instance reporting summary. Drives Completeness + Timeliness
   * (Now) at instance granularity. Outlook components still use
   * progress_entries (entry-level signals like latency trend, engagement).
   *
   * Empty array → Now lifecycle gate falls through to the existing
   * progress_entries-based path for backward compatibility with callers
   * that haven't migrated yet (only the test suite, post-migration).
   */
  expected_instances?: ExpectedInstanceInput[];
  reporting_cadence: ReportingCadenceInput;
  /** Default permissible-days-late grace window. Per DEV-846 default is 2. */
  grace_days: number;
  digest_acks: DigestAckInput[];
  preflight_items: PreflightItemInput[];
  phase_summary: PhaseSummaryInput;
  weights: ComponentWeights;
  /**
   * Previous-period result for trend computation. Optional — when omitted,
   * engine returns trend = 0. Caller can compute previous result by calling
   * the engine with as_of_date set to the prior period boundary.
   */
  previous_result?: {
    now_score: number | null;
    outlook_score: number | null;
  };
}

/* ------------------------------------------------------------------------- */
/*  Default weights                                                          */
/* ------------------------------------------------------------------------- */

/**
 * Engine-side defaults used when `weights` is not provided or is missing
 * fields. Per DEV-846 epic decision: equal weighting within each composite.
 *
 * Skip's note: defaults are sensible because all components are roughly
 * equal-importance signals. Per-project overrides via DEV-849 give power
 * users the knob without burdening defaults.
 */
export const DEFAULT_DATA_RELIABILITY_WEIGHTS: ComponentWeights = {
  now: {
    completeness: 0.5,
    timeliness: 0.5,
  },
  outlook: {
    entry_latency_trend: 0.25,
    engagement: 0.25,
    drift_risk: 0.25,
    preflight_risk: 0.25,
  },
};
